// =============================================================================
// src/lib/chunkReload.js - recover from a stale page bundle after a deploy.
//
// Views load on demand (React.lazy). Each deploy gives the bundles new names
// and removes the old ones, so a tab opened before a deploy asks for a file
// that no longer exists the first time it visits a page it has not loaded
// yet. The import rejects and, with nothing to catch it, React unmounted the
// whole app: the white screen that a refresh fixed.
//
//   lazyRetry(factory)      React.lazy that reloads the page once when the
//                           bundle is gone, instead of throwing.
//   isChunkLoadError(err)   true for "the file for this page did not load".
//   reloadForNewVersion()   one reload per 30 s (sessionStorage guard) so a
//                           real outage cannot loop.
//   installChunkErrorReload() handles Vite's vite:preloadError event too.
// =============================================================================
import { lazy } from "react";

const KEY = "practiceos.chunk_reload_at";
const WINDOW_MS = 30000;

export function isChunkLoadError(err) {
  const msg = String((err && (err.message || err)) || "");
  return /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|Unable to preload CSS|Loading chunk [\w-]+ failed|ChunkLoadError|is not a valid JavaScript MIME type|Expected a JavaScript/i.test(msg)
    || (err && err.name === "ChunkLoadError");
}

// Returns true when a reload was started.
export function reloadForNewVersion() {
  let last = 0;
  try { last = Number(sessionStorage.getItem(KEY) || 0); } catch (e) { /* storage blocked */ }
  if (Date.now() - last < WINDOW_MS) return false;
  try { sessionStorage.setItem(KEY, String(Date.now())); } catch (e) { /* storage blocked */ }
  window.location.reload();
  return true;
}

export function lazyRetry(factory) {
  return lazy(() => factory().catch((err) => {
    if (isChunkLoadError(err) && reloadForNewVersion()) {
      // Keep Suspense showing its fallback while the page reloads.
      return new Promise(() => {});
    }
    throw err;
  }));
}

export function installChunkErrorReload() {
  if (typeof window === "undefined") return;
  window.addEventListener("vite:preloadError", (event) => {
    if (reloadForNewVersion()) event.preventDefault();
  });
}
